/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable max-lines-per-function */
import { FC, lazy, Suspense } from "react";
import { Route, Routes } from "react-router-dom";
import { useDisallowRoute } from "../../common/hooks/useDisallowRoute";
import { getPartnerPathnameClient } from "../../getPartnerPathnameClient";
import { useLoaderContextA } from "../hooks/useLoaderContextA";
import { useLockToOrderSuccessPage } from "../hooks/useLockToOrderSuccessPage";
import { NoRouteMatch } from "./NoRouteMatch";
import { TryAgain } from "./TryAgain";
import { useSettingsValueData } from "../hooks/useSettingsValueData";
import { useOrderValueData } from "../hooks/useOrderValueData";
import { LoginRedirection } from "./LoginRedirection";
import { LoadingCircle } from "../../common/components/LoadingCircle";
import { SampleRoute } from "./SampleRoute";

// Lazy load route components to reduce initial bundle size and improve LCP
const Address = lazy(() => import("../../address/components/Address").then(m => ({ default: m.Address })));
const Cart = lazy(() => import("../../cart/components/Cart").then(m => ({ default: m.Cart })));
const Confirm = lazy(() => import("../../confirm/components/Confirm").then(m => ({ default: m.Confirm })));
const BillingAddressPage = lazy(() => import("../../payment/components/BillingAddressPage").then(m => ({ default: m.BillingAddressPage })));
const IFramePage = lazy(() => import("../../payment/components/IFramePage").then(m => ({ default: m.IFramePage })));
const IFrameSavedPage = lazy(() => import("../../payment/components/IFrameSavedPage").then(m => ({ default: m.IFrameSavedPage })));
const Payment = lazy(() => import("../../payment/components/Payment").then(m => ({ default: m.Payment })));
const Sample = lazy(() => import("../../sample/components/Sample").then(m => ({ default: m.Sample })));
const Shipping = lazy(() => import("../../shipping/components/Shipping").then(m => ({ default: m.Shipping })));
const Success = lazy(() => import("../../success/components/Success").then(m => ({ default: m.Success })));
const SampleUserRegistration = lazy(() => import("../../user-registration/components/SampleUserRegistration").then(m => ({ default: m.SampleUserRegistration })));

export const AppRoutes: FC = () => {
  useDisallowRoute();
  useLockToOrderSuccessPage();
  const settings = useSettingsValueData();
  const orders = useOrderValueData();
  const isExistsPurchasesParts =
    orders.tag === "ready" &&
    orders.data.orderLines?.some((line) => !line.isSample);
  const isExistsSampleParts =
    orders.tag === "ready" &&
    orders.data.orderLines?.some((line) => !!line.isSample);
  const allowPurchases = settings.data.pageIntegration?.allowPurchases;

  const loaderContext = useLoaderContextA();

  return loaderContext?.loader.tag === "errors" ? null : (
    <Suspense fallback={<div className="tw-flex tw-justify-center tw-items-center tw-min-h-screen"><LoadingCircle size="lg" /></div>}>
      <Routes>
        <Route
          path={`${getPartnerPathnameClient()}/cart`}
          element={<NoRouteMatch />}
        />

        {allowPurchases && (
         <Route
           path={`${getPartnerPathnameClient()}/purchases`}
           element={<Cart />}
         />
        )}

        {!allowPurchases && (
          <Route path={`${getPartnerPathnameClient()}/purchases`} element={<SampleRoute />}/>
        )}

        {settings.data.sampleOptions?.allowSamples && (
          <Route
            path={`${getPartnerPathnameClient()}/samples`}
            element={<Cart />}
          />
        )}

        <Route
          path={`${getPartnerPathnameClient()}/address`}
          element={<Address />}
        />
        <Route
          path={`${getPartnerPathnameClient()}/shipping`}
          element={<Shipping />}
        />

        {settings.data.sampleOptions?.allowSamples && isExistsSampleParts && (
          <Route
            path={`${getPartnerPathnameClient()}/sample-user-registration`}
            element={<SampleUserRegistration />}
          />
        )}

        {settings.data.sampleOptions?.allowSamples && isExistsSampleParts && (
          <Route
            path={`${getPartnerPathnameClient()}/sample-questions`}
            element={<Sample />}
          />
        )}
        <Route
          path={`${getPartnerPathnameClient()}/payment`}
          element={<NoRouteMatch />}
        />

        {isExistsPurchasesParts && (
          <Route
            path={`${getPartnerPathnameClient()}/payment`}
            element={<Payment />}
          >
            <Route path="billing" element={<BillingAddressPage />} />
            <Route path="card/entry" element={<IFramePage />} />
            <Route path="card/saved" element={<IFrameSavedPage />} />
          </Route>
        )}

        <Route
          path={`${getPartnerPathnameClient()}/confirm`}
          element={<Confirm />}
        />
        <Route
          path={`${getPartnerPathnameClient()}/success`}
          element={<Success />}
        />
        <Route
          path={`${getPartnerPathnameClient()}/login`}
          element={<LoginRedirection />}
        />
        <Route
          path={`${getPartnerPathnameClient()}/error`}
          element={<TryAgain />}
        />
        <Route path="*" element={<NoRouteMatch />} />
      </Routes>
    </Suspense>
  );
};
